import { inject, Injectable } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { combineLatest, Observable } from "rxjs";
import { map, shareReplay } from "rxjs/operators";

import { MrdmAuthApiService } from "./mrdm-auth-api.service";
import {
    APPLICATION_INSTANCE,
    AuthorizationExtendedPermission,
    AuthorizationOrganization,
    DPL_DATAENTRY_EDIT_PERMISSION,
    DPL_DATAENTRY_VIEW_PERMISSION,
    Permission,
    PRODUCT
} from "./mrdm-auth-api.types";

@Injectable({
    providedIn: "root"
})
export class MrdmAuthService {
    private _authorizationApiService = inject(MrdmAuthApiService);
    private _applicationInstance = inject(APPLICATION_INSTANCE);
    private _product = inject(PRODUCT);

    readonly user$ = this._authorizationApiService.getProfile().pipe(shareReplay(1));
    readonly userPermissions$ = this._searchPermissions$();
    readonly userOrganizations$ = this._getFilteredUserOrganizations$();
    private _userPermissions = toSignal(this.userPermissions$, {
        initialValue: [] as AuthorizationExtendedPermission[]
    });

    readonly isInternalUser$ = this.user$.pipe(map(profile => profile.isInternal));

    canEditUser(
        organizationId: number | undefined,
        productPermissions: string[]
    ): boolean {
        if (organizationId === undefined) {
            return false;
        }

        const hasDataEntryEditPermission = this.getProductPermissions(
            this._product,
            organizationId
        ).includes(DPL_DATAENTRY_EDIT_PERMISSION);
        const hasRegistryManagePermission = productPermissions.some(
            permission => this._getPermissionLevel(permission) === Permission.Manage
        );

        return hasDataEntryEditPermission && hasRegistryManagePermission;
    }

    canAccessProject(
        organizationId: number | undefined,
        projectProduct: string | undefined
    ): boolean {
        if (organizationId === undefined || !projectProduct) {
            return false;
        }

        return this.getProductPermissions(projectProduct, organizationId).some(
            permission => this._getPermissionLevel(permission) !== undefined
        );
    }

    private _getPermissionLevel(permission: string): Permission | undefined {
        const parts = permission.split("_");
        if (parts.length !== 3 || parts.some(part => part.length === 0)) {
            return undefined;
        }

        return Object.values(Permission).find(permissionLevel => permissionLevel === parts[2]);
    }

    getProductPermissions(
        projectProduct: string | undefined,
        organizationId: number | undefined
    ): string[] {
        return this._userPermissions()
            .filter(
                permission =>
                    permission.product === projectProduct &&
                    permission.organizationId === organizationId
            )
            .flatMap(permission => permission.permissions);
    }

    private _searchPermissions$(): Observable<AuthorizationExtendedPermission[]> {
        return this._authorizationApiService
            .searchPermissions({ applicationInstances: [this._applicationInstance] })
            .pipe(
                map(permissions => {
                    const dpPermissions = permissions.filter(
                        permission =>
                            permission.product === this._product &&
                            (permission.permissions.includes(DPL_DATAENTRY_VIEW_PERMISSION) ||
                                permission.permissions.includes(DPL_DATAENTRY_EDIT_PERMISSION))
                    );
                    return permissions.filter(perm =>
                        dpPermissions.some(dpPerm => dpPerm.organizationId === perm.organizationId)
                    );
                }),
                shareReplay(1)
            );
    }

    private _getFilteredOrganizationIdCodes$(): Observable<number[]> {
        return this.userPermissions$.pipe(
            map(permissions => {
                const filteredOrganizationIdCodes: number[] = permissions.map(item => item.organizationId)
                    .filter(organizationId => organizationId !== null) as number[];

                return [...new Set(filteredOrganizationIdCodes)];
            })
        );
    }

    private _getFilteredUserOrganizations$(): Observable<AuthorizationOrganization[]> {
        return combineLatest([this.user$, this._getFilteredOrganizationIdCodes$()]).pipe(
            map(([user, filteredOrganizationIdCodes]) => {
                const organizations = user.organizations;
                return organizations.filter(
                    organization =>
                        organization.organizationId !== null &&
                        filteredOrganizationIdCodes.includes(organization.organizationId)
                );
            })
        );
    }
}
